/**
 * Latest `waypoint_pred` alert per drone (ENG-86), plus the real arrival
 * timestamps `waypoint_approached` alerts report. Fed from `connector.tsx`
 * as `case 'waypoint'`: both messages come from their per-aircraft topics
 * (`waypoint_pred`, `waypoint_approached`, ENG-173). The alerts-key copy of
 * `waypoint_approached` goes to the Live Alerts feed only.
 *
 * Deliberately NOT merged into `useDronesStore`/`OiLiveEnvelope`: unlike
 * telemetry/c2/proximity/oi, this data doesn't arrive on a per-drone MQTT
 * topic keyed the same way, and only these two `source_event` values (not
 * every alert) should feed it — keeping it a separate store avoids
 * threading a source_event branch into the drones store's generic apply*
 * methods.
 */
import { log } from '@/lib/log';
import type { AlertEnvelope } from '@/lib/mqtt/schemas/alerts';
import { useDronesStore } from '@/lib/store/drones';
import type { ReachedInfo, WaypointPredAlert } from '@/lib/waypoints/progress';
import {
  LAP_WRAP_MARGIN,
  isLapWrap,
  resolveNextWaypointArrayIndex,
  seedPassedAtFromReached,
  seedReachedInfoFromHistory,
  stampApproached,
  untrackedBelowIndex,
} from '@/lib/waypoints/progress';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface WaypointProgressStore {
  latestPredByDrone: Record<string, WaypointPredAlert>;
  /** droneId -> waypoint array index -> real ISO arrival timestamp, from a
   *  `waypoint_approached` alert. Never a guessed/client-observed time —
   *  see `stampApproached`. */
  passedAtByDrone: Record<string, Record<number, string>>;
  /** droneId -> true once a `waypoint_approached` alert for that drone has
   *  carried a structured `waypoint_index` (ENG-40 ask #1 — see
   *  `lib/waypoints/progress.ts`'s module header). Drives
   *  `missedDetectionEnabled` on `buildWaypointProgress`: only safe to
   *  report a "missed" waypoint once we know indices for this drone are no
   *  longer guessed. Sticky for the session — never reset back to false. */
  structuredApproachedByDrone: Record<string, boolean>;
  /** droneId -> the route lap of the latest `waypoint_approached` (ENG-211).
   *  A higher lap means the mission looped, so earlier arrival times are
   *  dropped; a lower one is a stale/out-of-order alert and is ignored. */
  lapByDrone: Record<string, number>;
  /** droneId -> waypoint index -> BE's report for the CURRENT lap (closest
   *  approach + its `missed` verdict, ENG-211). Reset whenever the lap changes. */
  reachedByDrone: Record<string, Record<number, ReachedInfo>>;
  /** droneId -> waypoints below this index were never tracked (tracking began
   *  mid-route), so they read "not tracked", not "missed". Absent = unknown or
   *  everything tracked. See `untrackedBelowIndex`. */
  untrackedBelowByDrone: Record<string, number>;
  /** droneId -> BE has sent per-waypoint verdicts (`missed`) for this drone this
   *  session. Sticky across lap wraps; see `WaypointTracking.verdicts`. */
  verdictsByDrone: Record<string, boolean>;
  /** droneId -> set when the target wrapped from the end of the route back to
   *  the start (read off `waypoint_pred`) and no alert of the NEW lap has
   *  arrived yet. `waypoint_pred` and `waypoint_approached` travel on different
   *  topics: pred moves to waypoint 0 and ~0.5 s later the last alert of the lap
   *  that just ended (waypoint 19, carrying that whole lap's results) lands.
   *  Without this guard those results were painted onto the new lap and stayed
   *  until the new lap's first alert, ~3.5 min later (seen live, 2026-10-07).
   *  `endedLap` is the lap that finished, when known. */
  wrapPendingByDrone: Record<string, { endedLap: number | null }>;
  applyAlert: (a: AlertEnvelope) => void;
  clear: (droneId: string) => void;
}

export const useWaypointProgressStore = create<WaypointProgressStore>()(
  devtools(
    (set) => ({
      latestPredByDrone: {},
      passedAtByDrone: {},
      structuredApproachedByDrone: {},
      lapByDrone: {},
      reachedByDrone: {},
      untrackedBelowByDrone: {},
      verdictsByDrone: {},
      wrapPendingByDrone: {},
      applyAlert: (a) =>
        set((s) => {
          if (!a.drone_id) return s;
          const droneId = a.drone_id;

          if (a.source_event === 'waypoint_approached') {
            const waypoints = useDronesStore.getState().byId[droneId]?.oi?.waypoints ?? null;
            // Trust a.waypoint_index only when it's a real position in the
            // known route — or when the route isn't loaded yet at all, in
            // which case there's nothing to bound-check against. An
            // in-bounds route with an out-of-range index is a real BE data
            // problem; don't silently accept it as if it were valid.
            const structuredIndexInBounds =
              a.waypoint_index != null && (!waypoints || a.waypoint_index < waypoints.length);

            let approachedIdx: number | null = null;
            if (structuredIndexInBounds && a.waypoint_index != null) {
              // ENG-40 ask #1, fulfilled: BE sends the index directly on
              // waypoint_approached now, same field waypoint_pred already
              // uses — trust it, no need to infer from the last pred.
              approachedIdx = a.waypoint_index;
            } else if (a.waypoint_index == null) {
              // Fallback for BE sessions that haven't shipped the ask yet:
              // attribute this event to whatever waypoint the drone's last
              // waypoint_pred was tracking. See stampApproached's doc
              // comment.
              const lastPred = s.latestPredByDrone[droneId];
              if (lastPred && waypoints) {
                approachedIdx = resolveNextWaypointArrayIndex(waypoints, lastPred);
              }
            }
            // else: a.waypoint_index was provided but is out of bounds for
            // the known route. Don't trust it, and don't fall back to
            // inference either (that could mis-attribute this specific
            // alert) — an honest no-op is safer than guessing.

            // A late alert for the lap that just ended (see `wrapPendingByDrone`).
            // Same lap number when both are known; otherwise judged by the
            // waypoint it reports sitting at the very end of the route.
            const wrap = s.wrapPendingByDrone[droneId];
            if (wrap) {
              const alertLap = a.lap ?? null;
              const nearEnd =
                a.waypoint_index != null &&
                waypoints != null &&
                a.waypoint_index >= waypoints.length - 1 - LAP_WRAP_MARGIN;
              const stale =
                alertLap != null && wrap.endedLap != null ? alertLap === wrap.endedLap : nearEnd;
              if (stale) {
                log.debug(
                  'waypoints: ignored a late waypoint_approached from the lap that just ended',
                  {
                    droneId,
                    waypoint: a.waypoint_index,
                    alertLap,
                    endedLap: wrap.endedLap,
                  },
                );
                return s;
              }
            }

            // ENG-211: `lap` is BE's route loop count. A different lap than the
            // one we hold means the mission looped — or was re-planned and the
            // count restarted — so the previous lap's arrival times and verdicts
            // no longer apply. (Not guarded against a lower lap: a re-plan can
            // legitimately restart the count, and one MQTT topic keeps order.)
            const lap = a.lap ?? null;
            const prevLap = s.lapByDrone[droneId] ?? null;
            const lapChanged = lap != null && lap !== prevLap;
            const lapReset = lapChanged && prevLap != null;

            const prevPassedAt = lapReset ? {} : (s.passedAtByDrone[droneId] ?? {});
            const prevReached = lapReset ? {} : (s.reachedByDrone[droneId] ?? {});
            const routeLength = waypoints ? waypoints.length : null;
            // `reachedWaypoints`: every earlier "reached at" time and BE verdict
            // of THIS lap, so a dashboard opened mid-flight is complete from the
            // next alert. BE's data is authoritative, so seed first and only fall
            // back to the alert's own timestamp / flag for a waypoint the history
            // doesn't cover (older BE, or no `lap` to filter the list by).
            let nextPassedAt = prevPassedAt;
            let nextReached = prevReached;
            if (lap != null && a.reached_waypoints) {
              nextPassedAt = seedPassedAtFromReached(
                nextPassedAt,
                a.reached_waypoints,
                lap,
                routeLength,
              );
              nextReached = seedReachedInfoFromHistory(
                nextReached,
                a.reached_waypoints,
                lap,
                routeLength,
              );
            }
            const coveredByHistory =
              lap != null &&
              a.reached_waypoints?.some(
                (e) => e.lap === lap && e.waypoint_index === approachedIdx,
              ) === true;
            if (approachedIdx != null && !coveredByHistory) {
              nextPassedAt = stampApproached(nextPassedAt, approachedIdx, a.timestamp);
              if (a.missed != null) {
                const had = nextReached[approachedIdx];
                if (!had || had.missed !== a.missed || had.distanceM !== (a.distance_m ?? null)) {
                  nextReached = {
                    ...nextReached,
                    [approachedIdx]: { missed: a.missed, distanceM: a.distance_m ?? null },
                  };
                }
              }
            }
            // Where tracking began, from the list itself; only recomputed when
            // the alert carries a list. Cleared on a lap reset.
            let untrackedBelow = lapReset ? null : (s.untrackedBelowByDrone[droneId] ?? null);
            if (lap != null && a.reached_waypoints) {
              untrackedBelow = untrackedBelowIndex(a.reached_waypoints, lap);
            }
            const untrackedChanged = untrackedBelow !== (s.untrackedBelowByDrone[droneId] ?? null);
            const verdictsChanged =
              !s.verdictsByDrone[droneId] &&
              (a.missed != null || Object.values(nextReached).some((r) => r.missed != null));
            const wrapCleared = wrap != null;

            // A lap reset always rewrites the entries (they start from `{}`).
            const passedAtChanged = lapReset || nextPassedAt !== prevPassedAt;
            const reachedChanged = lapReset || nextReached !== prevReached;
            const structuredChanged =
              structuredIndexInBounds && !s.structuredApproachedByDrone[droneId];
            if (
              !passedAtChanged &&
              !reachedChanged &&
              !untrackedChanged &&
              !structuredChanged &&
              !verdictsChanged &&
              !wrapCleared &&
              !lapChanged
            ) {
              return s;
            }
            if (lapReset) {
              log.debug('waypoints: lap changed, cleared the previous lap', {
                droneId,
                from: prevLap,
                to: lap,
              });
            }
            log.debug('waypoints: applied waypoint_approached', {
              droneId,
              waypoint: approachedIdx,
              lap,
              missed: a.missed ?? null,
              distanceM: a.distance_m ?? null,
              history: a.reached_waypoints?.length ?? null,
              untrackedBelow,
            });
            const { [droneId]: _w, ...wrapRest } = s.wrapPendingByDrone;
            const { [droneId]: _u, ...untrackedRest } = s.untrackedBelowByDrone;
            return {
              passedAtByDrone: passedAtChanged
                ? { ...s.passedAtByDrone, [droneId]: nextPassedAt }
                : s.passedAtByDrone,
              reachedByDrone: reachedChanged
                ? { ...s.reachedByDrone, [droneId]: nextReached }
                : s.reachedByDrone,
              untrackedBelowByDrone: untrackedChanged
                ? untrackedBelow != null
                  ? { ...s.untrackedBelowByDrone, [droneId]: untrackedBelow }
                  : untrackedRest
                : s.untrackedBelowByDrone,
              structuredApproachedByDrone: structuredChanged
                ? { ...s.structuredApproachedByDrone, [droneId]: true }
                : s.structuredApproachedByDrone,
              lapByDrone:
                lap != null && lapChanged ? { ...s.lapByDrone, [droneId]: lap } : s.lapByDrone,
              verdictsByDrone: verdictsChanged
                ? { ...s.verdictsByDrone, [droneId]: true }
                : s.verdictsByDrone,
              wrapPendingByDrone: wrapCleared ? wrapRest : s.wrapPendingByDrone,
            };
          }

          if (a.source_event !== 'waypoint_pred') return s;
          if (a.waypoint_index == null || a.target_lat == null || a.target_lng == null) {
            // Confirmed-shape fields missing — nothing to act on. Never
            // guess at a prediction from a partial payload.
            return s;
          }
          const pred: WaypointPredAlert = {
            waypointIndex: a.waypoint_index,
            targetLat: a.target_lat,
            targetLng: a.target_lng,
            distanceMeters: a.distance_m ?? null,
            etaSeconds: a.eta_seconds ?? null,
            ts: new Date(a.timestamp).getTime(),
          };

          // Detect the mission restarting (ENG-86: the sim loops instead of
          // landing) and clear stale passedAt state so the visualisation
          // reflects that a new lap began, instead of staying "all done"
          // forever after the first one. See isLapWrap's doc comment for
          // why this check is narrow (both ends of the jump, not just any
          // decrease).
          const waypoints = useDronesStore.getState().byId[droneId]?.oi?.waypoints ?? null;
          let passedAtByDrone = s.passedAtByDrone;
          let structuredApproachedByDrone = s.structuredApproachedByDrone;
          let reachedByDrone = s.reachedByDrone;
          let untrackedBelowByDrone = s.untrackedBelowByDrone;
          let wrapPendingByDrone = s.wrapPendingByDrone;
          if (waypoints) {
            const prevPred = s.latestPredByDrone[droneId] ?? null;
            const prevIdx = prevPred ? resolveNextWaypointArrayIndex(waypoints, prevPred) : null;
            const nextIdx = resolveNextWaypointArrayIndex(waypoints, pred);
            if (isLapWrap(waypoints.length, prevIdx, nextIdx)) {
              wrapPendingByDrone = {
                ...wrapPendingByDrone,
                [droneId]: { endedLap: s.lapByDrone[droneId] ?? null },
              };
              log.debug('waypoints: lap wrap detected from the target index', {
                droneId,
                from: prevIdx,
                to: nextIdx,
                endedLap: s.lapByDrone[droneId] ?? null,
              });
              const { [droneId]: _dropped, ...rest } = passedAtByDrone;
              passedAtByDrone = rest;
              // A new lap: last lap's verdicts and tracking boundary are stale.
              const { [droneId]: _dropR, ...restReached } = reachedByDrone;
              reachedByDrone = restReached;
              const { [droneId]: _dropU, ...restUntracked } = untrackedBelowByDrone;
              untrackedBelowByDrone = restUntracked;
              // Also drop the "structured index confirmed" flag: it's sticky
              // across the session, but right at the wrap boundary we have
              // zero approached evidence yet for the new lap. Without this,
              // an early-lap waypoint below the new target reads "missed"
              // the instant the wrap is detected, before the drone has had
              // any chance to re-fly it. The very next real
              // waypoint_approached on the new lap re-sets it to true.
              const { [droneId]: _droppedStructured, ...restStructured } =
                structuredApproachedByDrone;
              structuredApproachedByDrone = restStructured;
            }
          }

          return {
            latestPredByDrone: { ...s.latestPredByDrone, [droneId]: pred },
            passedAtByDrone,
            structuredApproachedByDrone,
            reachedByDrone,
            untrackedBelowByDrone,
            wrapPendingByDrone,
          };
        }),
      clear: (droneId) =>
        set((s) => {
          const { [droneId]: _p, ...restPred } = s.latestPredByDrone;
          const { [droneId]: _a, ...restPassed } = s.passedAtByDrone;
          const { [droneId]: _si, ...restStructured } = s.structuredApproachedByDrone;
          const { [droneId]: _lap, ...restLap } = s.lapByDrone;
          const { [droneId]: _re, ...restReachedAll } = s.reachedByDrone;
          const { [droneId]: _un, ...restUntrackedAll } = s.untrackedBelowByDrone;
          const { [droneId]: _vd, ...restVerdicts } = s.verdictsByDrone;
          const { [droneId]: _wp, ...restWrap } = s.wrapPendingByDrone;
          return {
            verdictsByDrone: restVerdicts,
            wrapPendingByDrone: restWrap,
            reachedByDrone: restReachedAll,
            untrackedBelowByDrone: restUntrackedAll,
            latestPredByDrone: restPred,
            passedAtByDrone: restPassed,
            structuredApproachedByDrone: restStructured,
            lapByDrone: restLap,
          };
        }),
    }),
    { name: 'waypointProgress' },
  ),
);
